'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ChangeEvent } from 'react';
import { Icon } from '@/components/ui/icon';
import { NewTwitterIcon } from '@/components/ui/icon/brands';
import { ArrowUpRight01Icon, CancelCircleIcon, CheckmarkCircle02Icon, Loading03Icon } from '@/components/ui/icon/glyphs';
import { InputField } from '@/components/ui/input-field';
import { Label } from '@/components/ui/label';
import { SegmentedControl, type SegmentedControlItem } from '@/components/ui/segmented-control';
import type { UseHandleCheckResult } from '@/hooks/gifts/use-handle-check';
import type { UseXHandleCheckResult } from '@/hooks/gifts/use-x-handle-check';
import { cn } from '@/lib/ui/utils';
import { selectStepAttempted, useGiftFormStore } from '@/stores/gift-form';
interface SendGiftStep1Props {
  morfi: UseHandleCheckResult;
  x: UseXHandleCheckResult;
}

/**
 * Step 1 of the gift flow — pick the recipient.
 *
 * Owns: platform tabs (Morfi/X), handle input, live lookup feedback.
 * The orchestrator owns the debounced lookup hooks (single source of truth)
 * and Next/Back; this step only mutates the gift-form store.
 */
export function SendGiftStep1(t0) {
  const $ = _c(106);
  const {
    morfi,
    x
  } = t0;
  const t = useTranslations("gift.send");
  const tSteps = useTranslations("gift.send.steps");
  const platform = useGiftFormStore(_temp);
  const handle = useGiftFormStore(_temp2);
  const setPlatform = useGiftFormStore(_temp3);
  const setHandle = useGiftFormStore(_temp4);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectStepAttempted(1);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const attempted = useGiftFormStore(t1);
  const handleStatus = platform === "morfi" ? morfi.status : x.status;
  let t2;
  if ($[1] !== attempted || $[2] !== handle) {
    t2 = attempted && handle.trim().length === 0;
    $[1] = attempted;
    $[2] = handle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const showRequiredError = t2;
  let t3;
  if ($[4] !== t) {
    t3 = t("platform.morfi");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = {
      key: "morfi",
      label: t3
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("platform.x");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = {
      key: "x",
      icon: NewTwitterIcon,
      ariaLabel: t5
    };
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = [t4, t6];
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const platformItems = t7;
  let t8;
  if ($[15] !== t) {
    t8 = t("platform.label");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <Label htmlFor="gift-platform">{t8}</Label>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== setPlatform) {
    t10 = key => {
      if (key === "morfi" || key === "x") {
        setPlatform(key);
      }
    };
    $[19] = setPlatform;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== platform || $[22] !== platformItems || $[23] !== t10) {
    t11 = <SegmentedControl variant="tab-surface" asTabs={true} items={platformItems} active={platform} onChange={t10} layoutId="gift-platform-tabs-pill" fullWidth={true} className="rounded-pill bg-background-tertiary p-1" />;
    $[21] = platform;
    $[22] = platformItems;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t9) {
    t12 = <div className="flex flex-col gap-2">{t9}{t11}</div>;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t) {
    t13 = t("handle.label");
    $[28] = t;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t) {
    t14 = t("handle.placeholder");
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== setHandle) {
    t15 = e => setHandle(e.target.value.replace(/^@+/, ""));
    $[32] = setHandle;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const t16 = handleStatus === "not_found" || handleStatus === "self" || handleStatus === "opted_out" || handleStatus === "invalid_format";
  let t17;
  if ($[34] !== showRequiredError || $[35] !== tSteps) {
    t17 = showRequiredError ? tSteps("errors.handleRequired") : undefined;
    $[34] = showRequiredError;
    $[35] = tSteps;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const t18 = handleStatus === "found" && "border-feedback-success-border focus-visible:ring-feedback-success-border/30";
  let t19;
  if ($[37] !== t18) {
    t19 = cn(t18);
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <span className="text-foreground-secondary">@</span>;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== handleStatus) {
    t21 = handleStatus === "checking" ? <Icon icon={Loading03Icon} className="size-4 animate-spin text-foreground-secondary" /> : handleStatus === "found" ? <Icon icon={CheckmarkCircle02Icon} className="size-4 text-feedback-success-content" /> : handleStatus === "not_found" || handleStatus === "self" || handleStatus === "opted_out" || handleStatus === "invalid_format" ? <Icon icon={CancelCircleIcon} className="size-4 text-feedback-error-content" /> : null;
    $[40] = handleStatus;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== handle || $[43] !== t13 || $[44] !== t14 || $[45] !== t15 || $[46] !== t16 || $[47] !== t17 || $[48] !== t19 || $[49] !== t21) {
    t22 = <InputField id="gift-handle" label={t13} placeholder={t14} value={handle} onChange={t15} aria-invalid={t16} error={t17} autoComplete="off" spellCheck={false} className={t19} leftIcon={t20} rightIcon={t21} />;
    $[42] = handle;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
    $[48] = t19;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== handleStatus || $[52] !== t) {
    t23 = handleStatus === "checking" ? t("handle.checking") : null;
    $[51] = handleStatus;
    $[52] = t;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== handle || $[55] !== handleStatus || $[56] !== platform || $[57] !== t || $[58] !== x) {
    t24 = handleStatus === "found" ? t("handle.found", {
      handle: platform === "x" && x.canonicalScreenName !== null ? x.canonicalScreenName : handle
    }) : null;
    $[54] = handle;
    $[55] = handleStatus;
    $[56] = platform;
    $[57] = t;
    $[58] = x;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== handle || $[61] !== handleStatus || $[62] !== platform || $[63] !== t) {
    t25 = handleStatus === "not_found" ? t("handle.notFound", {
      handle,
      platform: platform === "morfi" ? t("platform.morfi") : t("platform.x")
    }) : null;
    $[60] = handle;
    $[61] = handleStatus;
    $[62] = platform;
    $[63] = t;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== handleStatus || $[66] !== t) {
    t26 = handleStatus === "self" ? t("handle.self") : null;
    $[65] = handleStatus;
    $[66] = t;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== handle || $[69] !== handleStatus || $[70] !== t) {
    t27 = handleStatus === "opted_out" ? t("handle.optedOut", {
      handle
    }) : null;
    $[68] = handle;
    $[69] = handleStatus;
    $[70] = t;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] !== handleStatus || $[73] !== t) {
    t28 = handleStatus === "invalid_format" ? t("handle.invalidFormat") : null;
    $[72] = handleStatus;
    $[73] = t;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== handleStatus || $[76] !== t) {
    t29 = handleStatus === "unavailable" ? t("handle.xUnavailable") : null;
    $[75] = handleStatus;
    $[76] = t;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== t23 || $[79] !== t24 || $[80] !== t25 || $[81] !== t26 || $[82] !== t27 || $[83] !== t28 || $[84] !== t29) {
    t30 = <output className="text-caption text-foreground-secondary">{t23}{t24}{t25}{t26}{t27}{t28}{t29}</output>;
    $[78] = t23;
    $[79] = t24;
    $[80] = t25;
    $[81] = t26;
    $[82] = t27;
    $[83] = t28;
    $[84] = t29;
    $[85] = t30;
  } else {
    t30 = $[85];
  }
  let t31;
  if ($[86] !== handleStatus || $[87] !== morfi || $[88] !== platform || $[89] !== t) {
    t31 = handleStatus === "found" && platform === "morfi" && morfi.recipientUserId !== null ? <><span className="text-caption text-foreground-tertiary" aria-hidden="true">•</span><a href={`/portfolio/${morfi.recipientUserId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-caption text-foreground-primary hover:underline">{t("handle.viewMorfiProfile")}<Icon icon={ArrowUpRight01Icon} className="size-3" aria-hidden="true" /></a></> : null;
    $[86] = handleStatus;
    $[87] = morfi;
    $[88] = platform;
    $[89] = t;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  let t32;
  if ($[91] !== handleStatus || $[92] !== platform || $[93] !== t || $[94] !== x) {
    t32 = handleStatus === "found" && platform === "x" && x.canonicalScreenName !== null ? <><span className="text-caption text-foreground-tertiary" aria-hidden="true">•</span><a href={`https://x.com/${x.canonicalScreenName}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-caption text-foreground-primary hover:underline">{t("handle.viewXProfile")}<Icon icon={NewTwitterIcon} className="size-3" aria-hidden="true" /></a></> : null;
    $[91] = handleStatus;
    $[92] = platform;
    $[93] = t;
    $[94] = x;
    $[95] = t32;
  } else {
    t32 = $[95];
  }
  let t33;
  if ($[96] !== t30 || $[97] !== t31 || $[98] !== t32) {
    t33 = <div className="flex flex-wrap items-center gap-x-2 gap-y-1">{t30}{t31}{t32}</div>;
    $[96] = t30;
    $[97] = t31;
    $[98] = t32;
    $[99] = t33;
  } else {
    t33 = $[99];
  }
  let t34;
  if ($[100] !== t22 || $[101] !== t33) {
    t34 = <div className="flex flex-col gap-2">{t22}{t33}</div>;
    $[100] = t22;
    $[101] = t33;
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  let t35;
  if ($[103] !== t12 || $[104] !== t34) {
    t35 = <div className="flex flex-col gap-4">{t12}{t34}</div>;
    $[103] = t12;
    $[104] = t34;
    $[105] = t35;
  } else {
    t35 = $[105];
  }
  return t35;
}
function _temp4(s_2) {
  return s_2.setHandle;
}
function _temp3(s_1) {
  return s_1.setPlatform;
}
function _temp2(s_0) {
  return s_0.handle;
}
function _temp(s) {
  return s.platform;
}